import React, { useCallback, useEffect, useState } from 'react';
import { styled } from '@mui/material';
import BusinessIcon from '@mui/icons-material/Business';
import { Invitation } from '@bimdata/definitions/dto';
import { UserStatus } from '@bimdata/definitions/const';
import { Avatar, LogoTitle, Span } from '@/components';
import { RootState, useAppSelector } from '@/state/store';
import { Button } from '@mui/material';
import { invitations as invitationList } from '@/loaders/user';
import { logo as fetchLogo } from '@/loaders/project';
import { avatar as fetchAvatar } from '@/loaders/user';
import { acceptInvitation, declineInvitation } from '@/actions/profile';
import { RequestStatus } from '@/contracts';
import { createSelector } from '@reduxjs/toolkit';
import t from '@/utils/translate';
import Prompt from '@/components/Prompt';

const Table = styled('table')({
  width: 'calc(100% - 40px)',
  borderCollapse: 'separate',
  borderSpacing: '0 10px',
  margin: '10px 20px',
  tableLayout: 'fixed',
});

const Row = styled('tr')({
  background: '#FAFAFA',
  height: '60px',
});

const Cell = styled('td')({
  padding: '10px',
  textAlign: 'left',
  ':first-of-type': {
    borderRadius: '10px 0 0 10px',
    paddingLeft: '20px',
    width: '320px',
  },
  ':last-child': {
    borderRadius: '0 10px 10px 0',
    paddingRight: '20px',
    textAlign: 'right',
    width: '230px',
    whiteSpace: 'nowrap',
  },
});

const Invitee = styled('div')({
  display: 'flex',
  flexDirection: 'row',
  alignItems: 'center',
  fontSize: '14px',
  lineHeight: '16px',
  color: '#42526E',
  whiteSpace: 'nowrap',
  width: '100%',
});

const InviteeAvatar = styled(Avatar)({
  width: '40px',
  height: '40px',
  marginRight: '10px',
});

const InviteeTitle = styled('div')({
  maxWidth: '250px',
});

const InviteeName = styled(Span)({
  fontWeight: 600,
});

const InvitationButton = styled(Button)({
  height: '32px',
  padding: '9px 16px',
  borderRadius: '6px',
  background: '#0052CC',
  border: '1px solid #0747A6',
  fontSize: '12px',
  lineHeight: '14px',
  color: '#FFFFFF',
  boxShadow: 'none'
});

type InvitationRowProps = {
  invitation: Invitation,
  onAccept: (i: Invitation) => Promise<void>,
  onDecline: (i: Invitation) => void,
  inviterAvatarSource: string | null,
  projectLogo: string | null,
};

const InvitationRow = ({ invitation, inviterAvatarSource, projectLogo, onAccept, onDecline }: InvitationRowProps) => {
  const acceptCallback = useCallback(() => onAccept(invitation), [ onAccept ]);
  const declineCallback = useCallback(() => onDecline(invitation), [ onDecline ]);

  return <Row key={invitation.id}>

    <Cell>
      <Invitee>
        <InviteeAvatar
          source={inviterAvatarSource}
          profile={invitation.inviter}
        />
        <InviteeTitle>
          <InviteeName>
            {invitation.inviter.status === UserStatus.ACTIVE
              ? invitation.inviter.fullName
              : (<del>{invitation.inviter.fullName}</del>)
            }
          </InviteeName>
          {t('invitation.message.invitesYou')}
        </InviteeTitle>
      </Invitee>
    </Cell>

    <Cell>
      <LogoTitle
        logoSource={projectLogo}
        placeholder={<BusinessIcon style={{ fontSize: '18px', verticalAlign: 'inherit' }}/>}
        sizeV={40}
        sizeH='fit-content'
        minWidth='180px'
        logoSizeH={71}
        fontSize={13}
        fontWeight={500}
      >
        {invitation.project.name}
      </LogoTitle>
    </Cell>

    <Cell>
      <InvitationButton
        variant='contained'
        sx={{
          marginRight: '10px',
        }}
        onClick={acceptCallback}
      >
        {t('invitation.action.accept')}
      </InvitationButton>
      
      <InvitationButton
        sx={{
          background: '#fff',
          color: '#091E42',
          borderColor: '#DFE1E6',
        }}
        onClick={declineCallback}
      >
        {t('invitation.action.refuse')}
      </InvitationButton>
    </Cell>

  </Row>
};

export default () => {
  const {
    logos, avatars, invitations
  } = useAppSelector(
    createSelector(
      [
        (state: RootState) => state.projects.logos,
        (state: RootState) => state.user.avatars,
        (state: RootState) => state.authentication.invitations,
      ],
      (logos, avatars, invitations) => ({ logos, avatars, invitations })
    )
  );

  const [ decliningInvitation, setDecliningInvitation ] = useState<Invitation | null>(null);

  const declineInvitationCallback = useCallback(async () => {
    if (decliningInvitation) {
      await declineInvitation(decliningInvitation);
    }
    setDecliningInvitation(null);
  }, [ invitations, decliningInvitation ]);

  const resetDecliningInvitationCallback = useCallback(() => setDecliningInvitation(null), []);

  useEffect(() => {
    if (!invitations) {
      invitationList();
    }
  }, [ invitations ]);

  useEffect(() => {
    if (invitations?.length) {
      for (const pd of invitations) {
        if (!logos[pd.project.code]) {
          fetchLogo(pd.project);
          return;
        }
        if (!avatars[pd.inviter.id]) {
          fetchAvatar(pd.inviter);
          return;
        }
      }
    }
  }, [ invitations, logos, avatars ]);

  if (!invitations?.length) {
    return <></>;
  }

  return (<>
    <Table>
      <tbody>
        {invitations.map((invitation: Invitation) => (
          <InvitationRow
            key={invitation.id}
            invitation={invitation}
            inviterAvatarSource={avatars[invitation.inviter.id]?.source || null}
            projectLogo={logos[invitation.project.code]?.source || null}
            onAccept={acceptInvitation}
            onDecline={setDecliningInvitation}
          />
        ))}
      </tbody>
    </Table>

    <Prompt
      open={!!decliningInvitation}
      title={t('invitation.title.confirm.decline')}
      question={t('invitation.hint.confirm.decline', { name: decliningInvitation?.project.name }, true)}
      onClose='cancel'
      onEnter='decline'
      actions={[
        {
          name: 'decline',
          handler: declineInvitationCallback,
          caption: t('invitation.action.refuse'),
          style: {
            background: '#0747A6',
            color: '#FFFFFF',
            border: 'none',
          }
        },
        {
          name: 'cancel',
          handler: resetDecliningInvitationCallback,
          caption: t('common.action.cancel'),
        }
      ]}
    />
  </>);
};
